<template>
    <div class="package-card">
        <div class="step-heading">How would you like to pay?</div>
        <div class="step-sub">Your {{ pkgPrice | money }} bundle has two parts: a bundle cost, and a per-clock-hour fee billed only as you complete your hours. Only the bundle cost is ever financed.</div>

        <div class="split-label">Every option costs the same {{ fmtRate(totalRate) }} per clock hour in total — choose how much you'd rather pay as clock hour fees as you complete, versus bundle cost now.</div>
        <div class="columns split-row">
            <div class="column" v-for="option in splitOptions" :key="'split-' + option.rate">
                <div class="split-card" :class="{'is-selected': pkgSplitUpfront === option.rate}" @click="setPkgSplit(option.rate)">
                    <div class="split-badge-row"><span class="split-badge" v-if="option.standard">Standard</span></div>
                    <div class="split-upfront">{{ fmtRate(option.deferredRate) }}<span class="rate-unit"> / clock hour</span></div>
                    <div class="split-meta">{{ fmtMoney(option.deferredTotal) }} later, as you complete</div>
                    <div class="split-deferred">
                        <div class="deferred-label">Bundle cost</div>
                        <div class="deferred-rate">{{ fmtMoney(option.upfrontTotal) }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="split-label mt-5">How would you like to cover the {{ pkgUpfront | money }} bundle cost?</div>
        <div class="columns">
            <div class="column">
                <div class="pay-card" :class="{'is-selected': pkgPay === 'full'}" @click="setPkgPay('full')">
                    <div class="pay-title">Pay bundle cost</div>
                    <div class="pay-price">{{ pkgUpfront | money }}</div>
                    <div class="pay-meta">one payment today</div>
                    <div class="pay-text">Your full bundle opens immediately, with nothing more due until your courses complete.</div>
                </div>
            </div>
            <div class="column">
                <div class="pay-card" :class="{'is-selected': pkgPay === 'plan'}" @click="setPkgPay('plan')">
                    <div class="pay-title">Deferred Payment Agreement</div>
                    <div class="pay-price">{{ pkgMonthly | money }}<span class="per-month"> / mo</span></div>
                    <div class="pay-meta">{{ pkgPlan.months }} monthly payments<template v-if="pkgMonthlyLast !== pkgMonthly"> &middot; final payment {{ pkgMonthlyLast | money }}</template></div>
                    <div class="pay-text">
                        Nothing is due today — your first payment isn't until <strong>{{ firstPaymentDate }}</strong>.
                        You will complete plan setup with our lending partner, The Payment Option, after review.
                    </div>
                </div>
            </div>
        </div>

        <div class="raise-callout" v-if="pkgPay === 'plan'">
            <div class="callout-icon"><i class="fad fa-arrow-trend-up"></i></div>
            <div>
                <div class="callout-title">Earn now, pay later — your raise arrives first</div>
                <div class="callout-text">Your first payment isn't until <strong>{{ firstPaymentDate }}</strong> — October 1 is when most districts' scale-advancement raises take effect. Finish your clock hours, move up your salary schedule, and let your new pay cover the payments.</div>
                <div class="callout-note">Requires completion of your clock hours and processing by your district before a pay raise can take effect.</div>
            </div>
        </div>

        <div class="deferred-note">
            <i class="far fa-info-circle mr-2"></i>The remaining {{ pkgDeferred | money }} is your clock hour fees — nothing is billed until you complete your courses. If for some reason you don't need this many clock hours, you don't need to pay for them later. The bundle cost, however, is non-refundable.
        </div>

        <div class="step-actions">
            <b-button type="is-text" @click="$emit('back')"><i class="fas fa-arrow-left mr-2"></i>Back</b-button>
            <b-button type="is-primary" size="is-medium" :disabled="!pkgPay" @click="$emit('continue')">Review Your Bundle<i class="fas fa-arrow-right ml-2"></i></b-button>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";
    import {SPLIT_OPTIONS, TERM_MONTHS, upfrontForHours, deferredForHours, firstPaymentDateFor} from "@/constants/packages";

    export default {
        name: "StepPayment",
        data: function() {
            return {
                termMonths: TERM_MONTHS
            };
        },
        computed: {
            ...mapGetters(['pkgHours', 'pkgPay', 'pkgPrice', 'pkgUpfront', 'pkgDeferred', 'pkgSplitUpfront', 'pkgSavings', 'pkgMonthly', 'pkgMonthlyLast', 'pkgPlan']),
            // Payments start on October 1 — the raise date (same math as the review)
            firstPaymentDate() {
                return firstPaymentDateFor(this.pkgHours).format('MMMM D, YYYY');
            },
            // All splits total the same true per-hour rate
            totalRate() {
                return SPLIT_OPTIONS[0].upfront + SPLIT_OPTIONS[0].deferred;
            },
            splitOptions() {
                return SPLIT_OPTIONS.map(option => ({
                    rate: option.upfront,
                    standard: !!option.standard,
                    deferredRate: option.deferred,
                    upfrontTotal: upfrontForHours(this.pkgHours, option.upfront),
                    deferredTotal: deferredForHours(this.pkgHours, option.upfront)
                }));
            }
        },
        methods: {
            ...mapMutations(['setPkgPay', 'setPkgSplit']),
            fmtMoney(value) {
                return '$' + Math.round(value).toLocaleString('en-US');
            },
            fmtRate(value) {
                return '$' + value.toFixed(2);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .split-label {
        font-size: 0.9rem;
        font-weight: bold;
        color: #33475b;
        margin: 1.4rem 0 0.6rem;
    }

    .split-row {
        margin-bottom: 0 !important;
    }

    .split-card {
        border: 2px solid #e1e7ee;
        border-radius: 6px;
        padding: 0.8rem 1rem;
        cursor: pointer;
        height: 100%;
        text-align: center;

        &:hover {
            border-color: #7fa8cd;
        }

        &.is-selected {
            border-color: #2e5f8f;
            background: #f2f7fc;
        }

        .split-badge-row {
            height: 18px;
            margin-bottom: 0.5rem;

            .split-badge {
                display: inline-block;
                background: #2e5f8f;
                color: #ffffff;
                font-size: 0.6rem;
                font-weight: 900;
                letter-spacing: 0.08em;
                text-transform: uppercase;
                padding: 2px 8px;
                border-radius: 999px;
            }
        }

        .split-upfront {
            font-size: 1.3rem;
            font-weight: 900;
            color: #2e5f8f;

            .rate-unit {
                font-size: 0.8rem;
                font-weight: bold;
                color: #666d7a;
            }
        }

        .split-meta {
            font-size: 0.75rem;
            color: #666d7a;
        }

        .split-deferred {
            margin-top: 0.5rem;
            border-top: 1px solid #eef1f5;
            padding-top: 0.5rem;

            .deferred-label {
                font-size: 0.65rem;
                font-weight: bold;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                color: #7a8a9d;
            }

            .deferred-rate {
                font-size: 0.85rem;
                font-weight: bold;
                color: #33475b;
                margin-top: 2px;
            }

            .deferred-total {
                font-size: 0.75rem;
                color: #666d7a;
            }
        }
    }

    .raise-callout {
        margin-top: 1rem;
        background: #f0f7f2;
        border: 1px solid #cfe5d6;
        border-radius: 6px;
        padding: 1rem 1.2rem;
        display: flex;
        gap: 0.9rem;

        .callout-icon {
            flex-shrink: 0;

            i {
                font-size: 1.4rem;
                color: #2e7d4f;
                margin-top: 2px;
            }
        }

        .callout-title {
            font-size: 0.9rem;
            font-weight: 900;
            color: #235f3c;
        }

        .callout-text {
            font-size: 0.85rem;
            color: #666d7a;
            margin-top: 0.3rem;
        }

        .callout-note {
            font-size: 0.75rem;
            color: #7a8a9d;
            margin-top: 0.4rem;
        }
    }

    .deferred-note {
        margin-top: 0.6rem;
        background: #f2f7fc;
        border-radius: 6px;
        padding: 0.8rem 1rem;
        font-size: 0.85rem;
        color: #666d7a;
    }

    .pay-card {
        border: 2px solid #e1e7ee;
        border-radius: 6px;
        padding: 1.2rem 1.4rem;
        cursor: pointer;
        height: 100%;

        &:hover {
            border-color: #7fa8cd;
        }

        &.is-selected {
            border-color: #2e5f8f;
            background: #f2f7fc;
        }

        .pay-title {
            font-size: 1rem;
            font-weight: 900;
            color: #33475b;
        }

        .pay-price {
            font-size: 1.6rem;
            font-weight: 900;
            color: #2e5f8f;
            margin-top: 0.5rem;

            .per-month {
                font-size: 0.9rem;
                font-weight: bold;
                color: #666d7a;
            }
        }

        .pay-meta {
            font-size: 0.85rem;
            color: #666d7a;
            margin-top: 2px;
        }

        .pay-text {
            font-size: 0.85rem;
            color: #666d7a;
            margin-top: 0.8rem;
        }
    }
</style>
